import { ApplicationRef, Component, Inject, inject, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { faqLists } from './components/faq/faq.component';
import { getReferrer, handleLastClickTracking, languages } from './shared/constants/app-enums';

declare global {
  interface Window {
    dataLayer: any[];
  }
}
export var selectedLang: string;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  canRender: boolean = true;
  onStaking = false;
  base = environment.domain;
  faqLists = faqLists;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private meta: Meta,
    private title: Title,
    private translate: TranslateService,
    private router: Router
  ) {
    inject(ApplicationRef).isStable.subscribe((isStable) => (this.canRender = isStable || this.canRender));

    handleLastClickTracking(['clickId', 'source', 'tid', 'pid']);
    getReferrer();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
      next: (event: NavigationEnd) => {
        this.onStaking = event.url.includes('staking');
        this.updateSEOData();
      }
    })
    this.translate.onLangChange.subscribe((params: LangChangeEvent) => {
      selectedLang = params.lang;
      this.updateSEOData();
      this.document.documentElement.lang = selectedLang; //making html lang dynamic for SEO
    });
  }

  ngOnInit(): void {
    this.updateMetaTags();
  }

  updateSEOData() {
    this.updateMetaData();
    this.updateCanonical();
    this.updateAlternateLinks();
    this.addFaqSchema();
    this.updateMetaTags()
  }

  updateMetaData() {
    const metaTitle = this.translate.instant(this.onStaking ? 'misc.meta_title_staking' : 'misc.meta_title');
    const metaData = this.translate.instant(this.onStaking ? 'misc.meta_desc_staking' : 'misc.meta_data');
    this.title.setTitle(metaTitle);
    this.meta.updateTag({ name: 'description', content: metaData });
  }

  updateCanonical() {
    const link: HTMLLinkElement | null = this.document.querySelector('link[rel=canonical]');
    let hrefValue = '';

    if (this.onStaking) {
      hrefValue = `${this.base}/${selectedLang}/staking`;
    } else if (selectedLang === 'en') {
      hrefValue = this.base;
    } else {
      hrefValue = `${this.base}/${selectedLang}`;
    }

    // Apply the canonical link
    if (link) {
      link.href = hrefValue;
    } else {
      const newLink = this.document.createElement('link');
      newLink.rel = 'canonical';
      newLink.href = hrefValue;
      this.document.head.appendChild(newLink);
    }
  }

  updateAlternateLinks() {
    const existingLinks = this.document.querySelectorAll('link[rel="alternate"]');
    existingLinks.forEach((link: HTMLLinkElement) => link.remove());
    for (let lang of languages) {
      const newLink = this.document.createElement('link');
      newLink.rel = 'alternate';
      newLink.hreflang = lang.value;
      let hrefValue = '';

      if (this.onStaking) {
        hrefValue = `${this.base}/${lang.value}/staking`;
      } else if (lang.value === 'en') {
        hrefValue = this.base;
      } else {
        hrefValue = `${this.base}/${lang.value}`;
      }
      newLink.href = hrefValue;
      this.document.head.appendChild(newLink);
    }
    const newLink = this.document.createElement('link');
    newLink.rel = 'alternate';
    newLink.hreflang = 'x-default';
    newLink.href = `${this.base}${this.onStaking ? '/en/staking' : ''}`;
    this.document.head.appendChild(newLink);
  }

  addFaqSchema() {
    if (this.onStaking) {
      this.document.getElementById('FAQ_SCHEMA')?.remove();
      return;
    }
    const keys: string[] = this.faqLists.flatMap((faq) => {
      return [faq.heading, faq.content]; // no spread needed
    });

    this.translate.get(keys).subscribe((translations) => {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faqLists.map((faq) => {
          const question = translations[faq.heading];
          const answer = translations[faq.content] || ''; // fallback to empty if undefined

          return {
            '@type': 'Question',
            name: question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer,
            },
          };
        }),
      };

      const existingScript = this.document.getElementById('FAQ_SCHEMA');
      if (existingScript) {
        this.document.getElementById('FAQ_SCHEMA')?.remove();
      }

      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'FAQ_SCHEMA';
      script.text = JSON.stringify(faqSchema);
      this.document.head.appendChild(script);
    });

  }

  updateMetaTags(): void {
    const titleText = this.translate.instant(this.onStaking ? 'misc.meta_title_staking' : 'misc.meta_title');
    const description = this.translate.instant(this.onStaking ? 'misc.meta_desc_staking' : 'misc.meta_data');
    const url = this.onStaking ? `${this.base}/${!selectedLang ? 'en' : selectedLang}/staking` : this.base;
    const image = `${this.base}/assets/images/png/og-logo.png`;

    // Open Graph meta tags
    this.meta.updateTag({ property: 'og:title', content: titleText });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });

    // Twitter Card meta tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: titleText });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:url', content: url });
    this.meta.updateTag({ name: 'twitter:site', content: '@Pepenode_io' });
  }
}
